import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Users, TrendingUp, BookOpen, Clock, Calendar, BarChart3, Eye, Star, Award, CheckCircle, XCircle, Filter, Mail } from 'lucide-react';
import AdminQuestHistory from '../components/AdminQuestHistory';
import AdminInvites from '../components/AdminInvites';
import AdminResetLink from '../components/AdminResetLink';
import { formatParisDate } from '../utils/formatParisDate';
import { getAdminCertificationAttempts, getAdminCertificationStats, getPlatformStats, getStudents } from '../services/api';

interface StudentStats {
  userId: string;
  username: string;
  totalExercises: number;
  completedExercises: number;
  averageScore: number;
  totalTimeSpent: number;
  lastActivity: string | null;
  lastVisit: string | null;
  recentExercises: Array<{
    exerciseId: string;
    title: string;
    score: number;
    completedAt: string;
    timeSpent: number;
  }>;
}

interface PlatformStats {
  totalUsers: number;
  totalExercises: number;
  totalSubmissions: number;
  averageCompletionRate: number;
  averageScore: number;
  conceptPopularity: Record<string, number>;
}

interface CertificationStats {
  totalAttempts: number;
  uniqueUsers: number;
  passedCount: number;
  passRate: number;
  averagePercentage: number;
  averageDurationMinutes: number;
}

interface CertificationAttempt {
  id: string;
  examId: string;
  userId: string;
  username: string;
  startedAt: string;
  finishedAt: string;
  submissionType: string;
  score: number;
  maxScore: number;
  passed: boolean;
  percentage: number;
  questionsAnswered: number;
  totalQuestions: number;
}

const AdminPage: React.FC = () => {
  const [studentStats, setStudentStats] = useState<StudentStats[]>([]);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [certificationStats, setCertificationStats] = useState<CertificationStats | null>(null);
  const [certificationAttempts, setCertificationAttempts] = useState<CertificationAttempt[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'quests' | 'certification' | 'invites'>('stats');
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>('all');
  const [modeFilter, setModeFilter] = useState<string>('all');

  const uniqueCertUsers = React.useMemo(() => {
    return Array.from(new Set(certificationAttempts.map(a => a.userId)))
      .map(userId => {
        const attempt = certificationAttempts.find(a => a.userId === userId);
        return { id: userId, name: attempt?.username || 'Inconnu' };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [certificationAttempts]);

  const filteredAttempts = React.useMemo(() => {
    return selectedUserFilter === 'all'
      ? certificationAttempts
      : certificationAttempts.filter(a => a.userId === selectedUserFilter);
  }, [selectedUserFilter, certificationAttempts]);

  useEffect(() => {
    fetchAdminData();
  }, [modeFilter]);

  const fetchAdminData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch platform statistics
      try {
        setPlatformStats(await getPlatformStats<PlatformStats>(modeFilter));
      } catch {
        throw new Error('Erreur lors du chargement des statistiques');
      }

      // Fetch student statistics
      try {
        setStudentStats(await getStudents<StudentStats>());
      } catch {
        throw new Error('Erreur lors du chargement des données étudiants');
      }

      // Fetch certification statistics (best-effort)
      try {
        setCertificationStats(await getAdminCertificationStats<CertificationStats>());
        setCertificationAttempts(await getAdminCertificationAttempts<CertificationAttempt>());
      } catch (certErr) {
        console.warn('Could not load certification data:', certErr);
      }
    } catch (err) {
      console.error('❌ Erreur admin:', err);
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (timeValue: number, isSeconds: boolean = false) => {
    // If timeValue is in seconds, convert to minutes; otherwise it's already in minutes
    const totalSeconds = isSeconds ? timeValue : timeValue * 60;

    if (totalSeconds < 60) {
      return `${Math.round(totalSeconds)}s`;
    }

    const totalMinutes = Math.round(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDate = formatParisDate;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Chargement des données administrateur...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-600 dark:text-red-400 mb-4">❌ {error}</div>
        <button
          onClick={fetchAdminData}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Tableau de bord administrateur</h1>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'stats'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Statistiques
            </button>
            <button
              onClick={() => setActiveTab('certification')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'certification'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
            >
              <Award className="w-4 h-4 inline mr-2" />
              Certifications
            </button>
            <button
              onClick={() => setActiveTab('quests')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'quests'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
            >
              <Star className="w-4 h-4 inline mr-2" />
              Gestion des Quêtes
            </button>
            <button
              onClick={() => setActiveTab('invites')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'invites'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Invitations
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'stats' ? (
          <>
            {/* Mode filter */}
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Mode :</span>
              <select
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">Tous les modes</option>
                <option value="unity_csharp">Unity C#</option>
                <option value="python_js">Python / JavaScript</option>
                <option value="godot">Godot / GDScript</option>
              </select>
            </div>
            {/* Platform Statistics */}
            {platformStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <Users className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Étudiants actifs</p>
                      <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{platformStats.totalUsers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <BookOpen className="w-8 h-8 text-green-600 dark:text-green-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-green-600 dark:text-green-400">Exercices générés</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-100">{platformStats.totalExercises}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <TrendingUp className="w-8 h-8 text-purple-600 dark:text-purple-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Taux de réussite</p>
                      <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{platformStats.averageCompletionRate.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <BarChart3 className="w-8 h-8 text-orange-600 dark:text-orange-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Score moyen</p>
                      <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">{platformStats.averageScore.toFixed(1)}/100</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Students List */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Progression des étudiants</h2>

                <div className="space-y-3">
                  {studentStats.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun étudiant inscrit pour le moment.</p>
                      <p className="text-sm">Invitez votre promo depuis l'onglet « Invitations ».</p>
                    </div>
                  )}
                  {studentStats.map((student) => (
                    <div
                      key={student.userId}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${selectedStudent?.userId === student.userId
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      onClick={() => setSelectedStudent(student)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {student.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">{student.username}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {student.completedExercises} exercices • Score moyen: {student.averageScore.toFixed(1)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 shrink-0">
                          <Clock className="w-4 h-4 mr-1" />
                          {formatDuration(student.totalTimeSpent)}
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Eye className="w-4 h-4 mr-1" />
                          <span>Dernière visite : {student.lastVisit ? formatDate(student.lastVisit) : 'Jamais'}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>Dernière copie : {student.lastActivity ? formatDate(student.lastActivity) : 'Jamais'}</span>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <span>Progression</span>
                          <span>{((student.completedExercises / Math.max(student.totalExercises, 1)) * 100).toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                            style={{
                              width: `${Math.min((student.completedExercises / Math.max(student.totalExercises, 1)) * 100, 100)}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Student Details */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                {selectedStudent ? (
                  <div>
                    <div className="flex items-center mb-4">
                      <Eye className="w-5 h-5 text-gray-400 mr-2" />
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Détails - {selectedStudent.username}
                      </h2>
                    </div>

                    {/* Student Summary */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-300">Exercices complétés</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedStudent.completedExercises}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-300">Score moyen</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedStudent.averageScore.toFixed(1)}/100</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-300">Temps total</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatDuration(selectedStudent.totalTimeSpent)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-300">Dernière visite</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {selectedStudent.lastVisit ? formatDate(selectedStudent.lastVisit) : 'Jamais'}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-300">Dernière copie</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {selectedStudent.lastActivity ? formatDate(selectedStudent.lastActivity) : 'Jamais'}
                        </p>
                      </div>
                    </div>

                    {/* Recent Exercises */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Exercices récents</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedStudent.recentExercises.length > 0 ? (
                        selectedStudent.recentExercises.map((exercise, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                <ReactMarkdown components={{
                                  p: ({ children }) => <span>{children}</span>,
                                  strong: ({ children }) => <strong className="font-bold text-blue-600 dark:text-blue-400">{children}</strong>,
                                  code: ({ children }) => <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{children}</code>
                                }}>
                                  {exercise.title}
                                </ReactMarkdown>
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(exercise.completedAt)}</p>
                            </div>
                            <div className="text-right">
                              <div className={`text-sm font-semibold ${exercise.score >= 70 ? 'text-green-600 dark:text-green-400' : exercise.score >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                                }`}>
                                {exercise.score}/100
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{formatDuration(exercise.timeSpent, true)}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm italic">Aucun exercice récent</p>
                      )}
                    </div>

                    <AdminResetLink
                      key={selectedStudent.userId}
                      userId={selectedStudent.userId}
                      username={selectedStudent.username}
                    />
                  </div>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Sélectionnez un étudiant pour voir ses détails</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : activeTab === 'certification' ? (
          <div className="space-y-6">
            {/* Certification Stats */}
            {certificationStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <Award className="w-8 h-8 text-indigo-600 dark:text-indigo-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">Total Tentatives</p>
                      <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">{certificationStats.totalAttempts}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-green-600 dark:text-green-400">Taux de Réussite</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-100">{certificationStats.passRate}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <TrendingUp className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Score Moyen</p>
                      <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">{certificationStats.averagePercentage}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <Clock className="w-8 h-8 text-purple-600 dark:text-purple-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Durée Moyenne</p>
                      <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{certificationStats.averageDurationMinutes}min</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Attempts Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Tentatives Récentes</h2>

                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <select
                    value={selectedUserFilter}
                    onChange={(e) => setSelectedUserFilter(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">Tous les utilisateurs</option>
                    {uniqueCertUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {filteredAttempts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Utilisateur</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Questions</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Résultat</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredAttempts.map((attempt) => (
                        <tr key={attempt.id}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                {attempt.username.charAt(0).toUpperCase()}
                              </div>
                              <span className="ml-3 font-medium text-gray-900 dark:text-white">{attempt.username}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(attempt.finishedAt)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            <span className={attempt.questionsAnswered < attempt.totalQuestions ? 'text-orange-500' : ''}>
                              {attempt.questionsAnswered}/{attempt.totalQuestions}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className={`text-sm font-semibold ${attempt.percentage >= 70 ? 'text-green-600 dark:text-green-400' :
                                attempt.percentage >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
                                  'text-red-600 dark:text-red-400'
                                }`}>
                                {attempt.percentage}%
                              </span>
                              <span className="ml-2 text-xs text-gray-400">({attempt.score}/{attempt.maxScore})</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {attempt.passed ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Réussi
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                <XCircle className="w-3 h-3 mr-1" />
                                Échoué
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 capitalize">
                            {attempt.examId?.startsWith('ai_training_') ? 'AI' : attempt.submissionType}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune tentative de certification pour le moment</p>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'quests' ? (
          <AdminQuestHistory />
        ) : activeTab === 'invites' ? (
          <AdminInvites />
        ) : null}
      </div>
    </div>
  );
};

export default AdminPage;